import { FC } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { usePermissions } from '@mint-lib/profile-helpers';

import {
  StaticCurrencySpan,
  StaticNumericSpan,
  StaticPercentSpan,
} from '@myn/mui';

import { ValueType } from '../../../../../../../types/_campaigns/campaignBuilder.js';
import { getDecimalLimit } from '../../GoalField/utils.js';
import { GoalFieldProps } from '../types.js';
import TextVariant, { StyledTruncation } from './TextVariant.jsx';

type Props = Pick<
  GoalFieldProps,
  'scale' | 'currency' | 'value' | 'placeholder'
>;

const GoalVariant: FC<Props> = ({ scale, currency, value, placeholder }) => {
  const { t } = useTranslation('@myn/planning');

  const { getPermissionField } = usePermissions();
  const isNonFinancialUser = !getPermissionField('financialDataFeature');
  const { goalLabel, goalValue, goalValueType, isLocked } = value;

  const isGoalOptionSelected = !!goalLabel && !!goalValueType;
  const isCurrencyValueHidden =
    isNonFinancialUser && goalValueType === ValueType.DECIMAL;
  const isNumericValue = goalValueType === ValueType.INT;
  const isPercentageValue = goalValueType === ValueType.PERCENT;
  const isCurrencyValue = goalValueType === ValueType.DECIMAL && !!currency;

  if (isLocked) {
    return <TextVariant scale={scale} value={t('Row Locked')} />;
  }

  if (!isGoalOptionSelected) {
    return <TextVariant scale={scale} value={null} placeholder={placeholder} />;
  }

  if (isCurrencyValueHidden) {
    return <TextVariant scale={scale} value={`${goalLabel}: ---`} />;
  }

  const goalValueDecimalLimit = getDecimalLimit(goalValueType);
  const staticSpanProps = {
    number: goalValue ?? 0,
    options: {
      minDecimals: goalValueDecimalLimit,
      maxDecimals: goalValueDecimalLimit,
    },
  };

  if (isNumericValue) {
    return (
      <StyledTruncation $scale={scale}>
        {`${goalLabel}: `}
        <StaticNumericSpan {...staticSpanProps} />
      </StyledTruncation>
    );
  }

  if (isPercentageValue) {
    return (
      <StyledTruncation $scale={scale}>
        {`${goalLabel}: `}
        <StaticPercentSpan {...staticSpanProps} />
      </StyledTruncation>
    );
  }

  if (isCurrencyValue) {
    return (
      <StyledTruncation $scale={scale}>
        {`${goalLabel}: `}
        <StaticCurrencySpan currency={currency} {...staticSpanProps} />
      </StyledTruncation>
    );
  }

  return (
    <StyledTruncation $scale={scale}>
      {`${goalLabel}: `}
      <StaticNumericSpan {...staticSpanProps} />
    </StyledTruncation>
  );
};

export default GoalVariant;
